/**
 * "You have work on this iPad. Bring it into your account?"
 *
 * Shown once, after a first sign-in, only when there is something to ask about.
 * Both answers are real answers and both are remembered — including "no", which
 * has to be, or the question comes back on every sign-in and becomes something
 * to swat away rather than read.
 *
 * What this screen will not do, in either direction: it does not upload
 * anything before the learner presses the button, and it does not remove the
 * local copy afterwards. The work stays on the iPad and stays usable offline.
 */

import { useEffect, useState } from 'react';
import { Button } from '../design/Button';
import { Alert, AuthLayout } from '../design/Feedback';
import { Icon } from '../design/Icon';
import { FailureSummary, SuccessPanel, useSubmission } from './formSupport';
import { useAuth } from './AuthProvider';
import { useSync } from '../sync/SyncProvider';
import { repositories } from '../storage/repositories';
import { createBackend } from '../curriculum/progress';
import {
  adoptLocalWork,
  declineAdoption,
  summariseLocalWork,
  type AdoptionResult,
  type LocalWorkSummary,
} from './localWork';
import type { LessonProgress } from '../curriculum/types';

export interface AdoptLocalWorkProps {
  /** Called once the question is settled, whichever way it was answered. */
  onDone: () => void;
}

export function AdoptLocalWork({ onDone }: AdoptLocalWorkProps) {
  const { state } = useAuth();
  const { engine } = useSync();
  const [summary, setSummary] = useState<LocalWorkSummary | null>(null);
  const [progress, setProgress] = useState<Record<string, LessonProgress>>({});
  const [result, setResult] = useState<AdoptionResult | null>(null);
  const [copied, setCopied] = useState<{ done: number; total: number } | null>(null);
  const { busy, failure, submit } = useSubmission();
  const userId = state.user?.id ?? '';

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const repos = repositories();
      const loaded = await createBackend().load();
      const found = await summariseLocalWork(repos, loaded, userId);
      if (cancelled) return;
      setProgress(loaded);
      setSummary(found);
      // Nothing to decide: do not show a question with no subject.
      if (found.total === 0) onDone();
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, onDone]);

  if (!summary || summary.total === 0) return null;

  if (result) {
    return (
      <AuthLayout heading="Your work is in your account">
        <SuccessPanel
          title={describeResult(result)}
          actions={
            <Button variant="primary" size="lg" onClick={onDone}>
              Start writing
            </Button>
          }
        >
          <p>
            The copy on this iPad is untouched, so everything keeps working offline exactly as it
            did before.
          </p>
          {result.alreadyThere > 0 ? (
            <p>
              {result.alreadyThere === 1 ? '1 item was' : `${result.alreadyThere} items were`}{' '}
              already in your account and did not need copying again.
            </p>
          ) : null}
          {result.failed > 0 ? (
            <Alert tone="info">
              {result.failed === 1 ? '1 item is' : `${result.failed} items are`} still queued. They
              are safe on this iPad and will finish syncing on their own.
            </Alert>
          ) : null}
        </SuccessPanel>
      </AuthLayout>
    );
  }

  const adopt = async () => {
    await submit(async () => {
      if (!engine) return;
      const outcome = await adoptLocalWork(
        engine,
        repositories(),
        progress,
        userId,
        summary,
        { onProgress: (done, total) => setCopied({ done, total }) },
      );
      setResult(outcome);
    });
    setCopied(null);
  };

  const decline = async () => {
    await submit(async () => {
      await declineAdoption(repositories(), userId, summary);
      onDone();
    });
  };

  return (
    <AuthLayout
      heading="Bring this iPad's work into your account?"
      sub="You wrote this before signing in. Nothing has been uploaded yet."
    >
      <FailureSummary failure={failure} />

      <ul className="mm-adopt__list">
        {summary.documents.map((document) => (
          <li key={document.id} className="mm-adopt__item">
            <span className="mm-adopt__icon" aria-hidden="true">
              <Icon name="pen" size={17} />
            </span>
            <span className="mm-adopt__name">{document.title || 'Untitled'}</span>
            <span className="mm-adopt__meta">{describeWhen(document.updatedAt)}</span>
          </li>
        ))}
        {summary.lessons.length > 0 ? (
          <li className="mm-adopt__item">
            <span className="mm-adopt__icon" aria-hidden="true">
              <Icon name="check" size={17} />
            </span>
            <span className="mm-adopt__name">
              Lesson progress for{' '}
              {summary.lessons.length === 1 ? '1 lesson' : `${summary.lessons.length} lessons`}
            </span>
          </li>
        ) : null}
      </ul>

      {copied ? (
        <p className="mm-auth__note" role="status">
          Copying {copied.done} of {copied.total}…
        </p>
      ) : null}

      <div className="mm-auth__actions">
        <Button variant="primary" size="lg" block loading={busy} onClick={() => void adopt()}>
          Bring it in
        </Button>
        <Button variant="tertiary" block disabled={busy} onClick={() => void decline()}>
          Keep it on this iPad only
        </Button>
      </div>
    </AuthLayout>
  );
}

function describeResult(result: AdoptionResult): string {
  const parts: string[] = [];
  if (result.documentsCopied > 0) {
    parts.push(result.documentsCopied === 1 ? '1 document' : `${result.documentsCopied} documents`);
  }
  if (result.lessonsCopied > 0) {
    parts.push(result.lessonsCopied === 1 ? '1 lesson' : `${result.lessonsCopied} lessons`);
  }
  if (parts.length === 0) return 'Nothing needed copying';
  return `Copied ${parts.join(' and ')}`;
}

function describeWhen(timestamp: number): string {
  const days = Math.floor((Date.now() - timestamp) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  return new Date(timestamp).toLocaleDateString();
}
